(() => {
  if (window.__poposoapSeriesTemplateBound) return;
  window.__poposoapSeriesTemplateBound = true;

  const BRAND_GREEN = '#4AB243';

  const track = (eventName, detail = {}) => {
    const payload = { event: eventName, ...detail };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    document.dispatchEvent(new CustomEvent('poposoap:analytics', { detail: payload }));

    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(eventName, detail);
    }
  };

  const routesRoot = () => window.Shopify?.routes?.root || '/';
  const route = (path) => `${routesRoot()}${path}`.replace(/\/{2,}/g, '/');
  const escapeHTML = (value = '') => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[char]);

  const quickViewState = {
    product: null,
    variant: null,
    mediaIndex: 0,
    lastTrigger: null,
    hydrationToken: 0,
    infoPanels: {},
    swipeSuppressClickUntil: 0
  };

  const productDetailCache = new Map();
  const mediaPreloadCache = new Set();
  const externalScriptCache = new Set();

  const getQuickView = () => {
    let quickView = document.querySelector('[data-pop-quick-view]');

    if (!quickView) {
      quickView = document.createElement('div');
      quickView.className = 'pop-series-qv';
      quickView.setAttribute('data-pop-quick-view', '');
      quickView.setAttribute('aria-hidden', 'true');
      document.body.appendChild(quickView);
    }

    return quickView;
  };

  const productFromTrigger = (trigger) => {
    const card = trigger.closest('[data-pop-series-card]');
    const script = card?.querySelector('[data-pop-quick-view-json]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Unable to parse POPOSOAP quick view data.', error);
      return null;
    }
  };

  const selectedVariantFor = (product) => {
    if (!product?.variants?.length) return null;
    return product.variants.find((variant) => String(variant.id) === String(product.defaultVariantId))
      || product.variants.find((variant) => variant.available)
      || product.variants[0];
  };

  const selectedMediaIndexFor = (product, variant) => {
    if (!variant?.imageSrc || !product?.media?.length) return 0;
    const index = product.media.findIndex((media) => media.src === variant.imageSrc);
    return index >= 0 ? index : 0;
  };

  const textFromHTML = (html = '') => {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    return doc.body.textContent?.replace(/\s+/g, ' ').trim() || '';
  };

  const listItemsFromHTML = (html = '') => {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    const listItems = [...doc.querySelectorAll('li')]
      .map((node) => node.textContent?.replace(/\s+/g, ' ').trim())
      .filter((item) => item && item.length > 12);

    if (listItems.length) return [...new Set(listItems)].slice(0, 8);

    return (textFromHTML(html).match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [])
      .map((item) => item.trim())
      .filter((item) => item.length > 28)
      .slice(0, 8);
  };

  const iconSVG = (name) => {
    const icons = {
      target: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8"></circle><circle cx="12" cy="12" r="3"></circle><path d="M12 2v3M12 19v3M2 12h3M19 12h3"></path></svg>',
      truck: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7h11v9H3z"></path><path d="M14 10h4l3 3v3h-7z"></path><circle cx="7" cy="18" r="2"></circle><circle cx="18" cy="18" r="2"></circle></svg>',
      shield: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z"></path></svg>',
      return: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 7L5 11l4 4"></path><path d="M5 11h9a5 5 0 1 1 0 10h-2"></path></svg>',
      support: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 13v-1a7 7 0 0 1 14 0v1"></path><path d="M5 13h3v6H5zM16 13h3v6h-3z"></path><path d="M16 19c0 2-2 3-4 3"></path></svg>',
      leaf: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 4c-8 0-14 5-14 12 0 2 1 4 3 4 7 0 11-8 11-16z"></path><path d="M6 20c3-6 7-9 12-12"></path></svg>',
      info: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v6M12 7h.01"></path></svg>',
      minus: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"></path></svg>',
      plus: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>',
      close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>',
      chevronLeft: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6"></path></svg>',
      chevronRight: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6"></path></svg>'
    };

    return icons[name] || icons.info;
  };

  const currentUnitPriceCents = () => {
    const variant = quickViewState.variant;
    const product = quickViewState.product;
    const value = Number(variant?.priceCents ?? product?.priceCents ?? 0);
    return Number.isFinite(value) ? value : 0;
  };

  const formatMoney = (cents, product = quickViewState.product) => {
    const value = Number(cents);
    if (!Number.isFinite(value) || value <= 0) {
      return product?.price || '';
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: product?.currencyCode || 'USD',
        currencyDisplay: 'narrowSymbol'
      }).format(value / 100);
    } catch (error) {
      return `$${(value / 100).toFixed(2)}`;
    }
  };

  const updateQuickViewTotalPrice = () => {
    const price = getQuickView().querySelector('[data-pop-qv-bar-price]');
    if (!price) return;
    price.textContent = formatMoney(currentUnitPriceCents() * getQuickViewQuantity());
  };

  const preloadImage = (src) => {
    if (!src || mediaPreloadCache.has(src)) return;
    mediaPreloadCache.add(src);
    const image = new Image();
    image.decoding = 'async';
    image.src = src;
  };

  const preloadAdjacentMedia = () => {
    const media = quickViewState.product?.media || [];
    if (!media.length) return;
    [-1, 0, 1].forEach((offset) => {
      const item = media[(quickViewState.mediaIndex + offset + media.length) % media.length];
      preloadImage(item?.src);
      preloadImage(item?.zoomSrc);
    });
  };

  const sanitizeExternalBlock = (node) => {
    if (!node) return '';
    const clone = node.cloneNode(true);
    clone.querySelectorAll('script, noscript, iframe').forEach((item) => item.remove());
    clone.querySelectorAll('img').forEach((img) => {
      img.loading = 'lazy';
      img.decoding = 'async';
      if (!img.alt) img.alt = '';
    });
    return clone.innerHTML.trim();
  };

  const sanitizeExternalNodeHTML = (node) => {
    if (!node) return '';
    const clone = node.cloneNode(true);
    clone.querySelectorAll('script, noscript, iframe').forEach((item) => item.remove());
    clone.querySelectorAll('img').forEach((img) => {
      img.loading = 'lazy';
      img.decoding = 'async';
      if (!img.alt) img.alt = '';
    });
    return clone.outerHTML.trim();
  };

  const refreshExternalRecommendationWidgets = () => {
    document.dispatchEvent(new CustomEvent('shopify:section:load', {
      detail: { sectionId: 'poposoap-quick-view' }
    }));
    document.dispatchEvent(new CustomEvent('selleasy:refresh'));

    ['Selleasy', 'SelleasyWidget', 'lbSelleasy', 'LogBaseSelleasy'].forEach((name) => {
      const api = window[name];
      if (!api) return;
      ['init', 'render', 'refresh', 'load'].forEach((method) => {
        if (typeof api[method] === 'function') {
          try {
            api[method]();
          } catch (error) {
            // Third-party widget APIs are not standardized; ignore failed probes.
          }
        }
      });
    });
  };

  const hasRenderableRecommendationContent = (target) => {
    if (!target) return false;

    const clone = target.cloneNode(true);
    clone.querySelectorAll('style, script, noscript, template').forEach((node) => node.remove());
    const text = clone.textContent?.replace(/\s+/g, ' ').trim() || '';
    const hasCommerceControl = Boolean(clone.querySelector('img[src], button, input, select, a[href*="/products/"]'));

    return hasCommerceControl || /You May Also Need|Add bundle to cart|Frequently Bought/i.test(text);
  };

  const validateRecommendationTarget = (target, token) => {
    window.setTimeout(() => {
      if (quickViewState.hydrationToken !== token || !target?.isConnected) return;
      if (hasRenderableRecommendationContent(target)) return;
      target.innerHTML = '';
      target.hidden = true;
    }, 6000);
  };

  const loadExternalScriptOnce = (src) => {
    const exists = [...document.querySelectorAll('script[src]')].some((script) => script.src === src || script.getAttribute('src') === src);
    if (!src || externalScriptCache.has(src) || exists) {
      return Promise.resolve();
    }

    externalScriptCache.add(src);

    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.onload = resolve;
      script.onerror = resolve;
      document.head.appendChild(script);
    });
  };

  const loadRecommendationScripts = (doc) => {
    const scripts = [...doc.querySelectorAll('script[src]')]
      .map((script) => script.getAttribute('src'))
      .filter((src) => /selleasy|lb-upsell|lb-selleasy/i.test(src || ''));

    return Promise.all([...new Set(scripts)].map(loadExternalScriptOnce));
  };

  const registerInfoPanel = (id, title, html) => {
    quickViewState.infoPanels[id] = { title, html };

    return `
      <button class="pop-series-qv__info-button" type="button" aria-label="More about ${escapeHTML(title)}" data-pop-qv-info="${escapeHTML(id)}">
        ${iconSVG('info')}
      </button>
    `;
  };

  const openInfoPanel = (id) => {
    const panel = quickViewState.infoPanels[id];
    const quickView = getQuickView();
    const dialog = quickView.querySelector('[data-pop-qv-info-dialog]');
    if (!panel || !dialog) return;

    dialog.querySelector('[data-pop-qv-info-title]').textContent = panel.title;
    dialog.querySelector('[data-pop-qv-info-content]').innerHTML = panel.html;
    dialog.hidden = false;
    dialog.querySelector('[data-pop-qv-info-close]')?.focus({ preventScroll: true });
  };

  const closeInfoPanel = () => {
    const dialog = getQuickView().querySelector('[data-pop-qv-info-dialog]');
    if (!dialog) return;
    dialog.hidden = true;
  };

  const quickViewBadgesHTML = (product) => {
    const badges = [];

    if (product.badges?.hot) {
      badges.push('<span class="pop-series-qv__badge pop-series-qv__badge--hot">&#9733; HOT</span>');
    }

    if (product.badges?.sale) {
      badges.push('<span class="pop-series-qv__badge pop-series-qv__badge--sale">% SALE</span>');
    }

    if (product.badges?.custom) {
      badges.push(`<span class="pop-series-qv__badge pop-series-qv__badge--hot">${escapeHTML(product.badges.custom)}</span>`);
    }

    return badges.length ? `<div class="pop-series-qv__badge-row">${badges.join('')}</div>` : '';
  };

  const variantPriceHTML = (variant, product) => {
    const price = variant?.price || product?.price || '';
    const compare = variant?.compareAtPrice || product?.compareAtPrice || '';
    const save = variant?.saveAmount || product?.saveAmount || '';

    return `
      <span class="pop-series-qv__price" data-pop-qv-price>${escapeHTML(price)}</span>
      ${compare ? `<span class="pop-series-qv__compare" data-pop-qv-compare>${escapeHTML(compare)}</span>` : '<span class="pop-series-qv__compare" data-pop-qv-compare hidden></span>'}
      ${save ? `<span class="pop-series-qv__save" data-pop-qv-save>Save ${escapeHTML(save)}</span>` : '<span class="pop-series-qv__save" data-pop-qv-save hidden></span>'}
    `;
  };

  const quickViewThumbsHTML = (media = []) => media.map((item, index) => `
    <button class="pop-series-qv__thumb" type="button" data-pop-qv-media="${index}" aria-label="View product image ${index + 1}" aria-current="${index === quickViewState.mediaIndex}">
      <img src="${escapeHTML(item.thumb || item.src)}" data-pop-qv-fallback-src="${escapeHTML(item.src || '')}" alt="${escapeHTML(item.alt || '')}" loading="lazy">
    </button>
  `).join('');

  const quickViewVariantHTML = (product) => {
    if (!product.variants || product.variants.length <= 1) return '';

    return `
      <label class="pop-series-qv__variant">
        <span>Choose option</span>
        <select data-pop-qv-variant>
          ${product.variants.map((variant) => `
            <option value="${escapeHTML(variant.id)}" ${String(variant.id) === String(quickViewState.variant?.id) ? 'selected' : ''} ${variant.available ? '' : 'disabled'}>
              ${escapeHTML(variant.title)}${variant.available ? '' : ' - Sold out'}
            </option>
          `).join('')}
        </select>
      </label>
    `;
  };

  const couponCardHTML = (coupon = {}) => {
    if (coupon.html) {
      return `<div class="pop-series-qv__coupon-native" data-pop-qv-coupon-card>${coupon.html}</div>`;
    }

    const amount = coupon.amount;
    const code = coupon.code;
    const message = coupon.message || (amount ? `Save ${amount} with our discount code.` : '');
    if (!amount && !code && !message) return '';
    const timer = coupon.timer ? `<span class="pop-series-qv__coupon-expire">${escapeHTML(coupon.timer)}</span>` : '';

    return `
      <div class="pop-series-qv__coupon" data-pop-qv-coupon-card>
        <div class="pop-series-qv__coupon-value">
          <span>Save</span>
          <strong>${escapeHTML(amount)}</strong>
          <span>OFF</span>
        </div>
        <div class="pop-series-qv__coupon-copy">
          <strong>${escapeHTML(message)}</strong>
          <span class="pop-series-qv__coupon-code-row">
            <span>${escapeHTML(code)}</span>
            <button type="button" data-pop-qv-copy-code="${escapeHTML(code)}">COPY CODE</button>
          </span>
          ${timer}
        </div>
      </div>
    `;
  };

  const quickViewCouponHTML = (product) => {
    const coupon = product.defaultCoupon;
    const html = coupon?.enabled ? couponCardHTML(coupon) : '';

    return `
      <div class="pop-series-qv__coupon-slot" data-pop-qv-coupon ${html ? '' : 'hidden'}>
        ${html}
      </div>
    `;
  };

  const extractCouponFromProductPage = (doc) => {
    const card = doc.querySelector('.coupon-card, [class*="coupon" i][data-end-time], [class*="coupon" i][data-end-ts]');
    if (!card) return null;
    if (isExpiredCouponCard(card)) return null;

    const faqId = card.querySelector('.coupon-info-btn')?.dataset.faqId;
    const modal = faqId ? doc.getElementById(faqId) : null;
    const html = [sanitizeExternalNodeHTML(card), sanitizeExternalNodeHTML(modal)].filter(Boolean).join('');
    const amount = card.querySelector('.coupon-amount')?.textContent?.replace(/\s+/g, ' ').trim();
    const code = card.querySelector('.coupon-code')?.textContent?.replace(/\s+/g, ' ').trim()
      || card.querySelector('[data-code]')?.getAttribute('data-code');
    const message = card.querySelector('.coupon-message')?.textContent?.replace(/\s+/g, ' ').trim();
    const timer = card.querySelector('.coupon-expire')?.textContent?.replace(/\s+/g, ' ').trim();
    const endTime = couponEndTimestamp(card);

    if (!html && !amount && !code && !message) return null;

    return {
      html,
      amount,
      code,
      message,
      timer,
      endTime
    };
  };

  const setQuickViewCoupon = (coupon) => {
    const quickView = getQuickView();
    const slot = quickView.querySelector('[data-pop-qv-coupon]');
    if (!slot) return;

    if (!coupon) {
      slot.innerHTML = '';
      slot.hidden = true;
      return;
    }

    const html = couponCardHTML(coupon);
    if (!html) {
      slot.innerHTML = '';
      slot.hidden = true;
      return;
    }

    slot.innerHTML = html;
    slot.hidden = false;
    initQuickViewCoupons(slot);
  };

  const couponEndTimestamp = (card) => {
    if (!card) return null;

    const rawEnd = card.dataset.endTime
      || card.dataset.endTs
      || card.dataset.endTimestamp
      || card.dataset.expireAt
      || card.getAttribute('data-end-time')
      || card.getAttribute('data-end-ts')
      || card.getAttribute('data-end-timestamp')
      || card.getAttribute('data-expire-at');

    if (!rawEnd) return null;

    const numericEnd = Number(rawEnd);
    const endTime = Number.isFinite(numericEnd)
      ? (numericEnd < 10000000000 ? numericEnd * 1000 : numericEnd)
      : Date.parse(rawEnd);

    return Number.isFinite(endTime) ? endTime : null;
  };

  const formatCouponCountdown = (milliseconds) => {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor(totalSeconds / 3600) % 24;
    const minutes = Math.floor(totalSeconds / 60) % 60;
    const seconds = totalSeconds % 60;

    return `${days} Days ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const hideQuickViewCouponCard = (card) => {
    const slot = card?.closest('[data-pop-qv-coupon]');
    if (!slot) return;
    slot.innerHTML = '';
    slot.hidden = true;
  };

  const initQuickViewCoupons = (root = getQuickView()) => {
    root.querySelectorAll('.coupon-card').forEach((card) => {
      if (card.dataset.popQvCountdownBound === 'true') return;
      const endTime = couponEndTimestamp(card);
      const timer = card.querySelector('.coupon-timer');

      if (!endTime) {
        if (timer) timer.textContent = '--';
        return;
      }

      let intervalId;
      const tick = () => {
        if (!card.isConnected) {
          window.clearInterval(intervalId);
          return false;
        }

        const remaining = endTime - Date.now();
        if (remaining <= 0) {
          window.clearInterval(intervalId);
          if (timer) timer.textContent = 'Expired';
          hideQuickViewCouponCard(card);
          return false;
        }

        if (timer) timer.textContent = formatCouponCountdown(remaining);
        return true;
      };

      card.dataset.popQvCountdownBound = 'true';
      if (tick()) {
        intervalId = window.setInterval(tick, 1000);
      }
    });

    root.querySelectorAll('.coupon-info-btn').forEach((button) => {
      if (button.dataset.popQvIconNormalized === 'true') return;
      button.dataset.popQvIconNormalized = 'true';
      button.setAttribute('aria-label', button.getAttribute('aria-label') || 'Discount code FAQ');
      button.innerHTML = iconSVG('info');
    });

    root.querySelectorAll('.coupon-modal__close').forEach((button) => {
      if (button.dataset.popQvIconNormalized === 'true') return;
      button.dataset.popQvIconNormalized = 'true';
      button.setAttribute('aria-label', button.getAttribute('aria-label') || 'Close discount code FAQ');
      button.innerHTML = iconSVG('close');
    });
  };

  const isExpiredCouponCard = (card) => {
    const stateText = [
      card.getAttribute('aria-label'),
      card.getAttribute('class'),
      card.textContent
    ].filter(Boolean).join(' ');

    if (/\b(expired|ended|inactive|disabled)\b/i.test(stateText)) return true;

    const endTime = couponEndTimestamp(card);

    return Number.isFinite(endTime) && endTime <= Date.now();
  };

  const quickViewKeyFeaturesHTML = (product) => {
    const features = [
      ...listItemsFromHTML(product.descriptionHtml),
      ...(Array.isArray(product.features) ? product.features : [])
    ].filter(Boolean);

    const uniqueFeatures = [...new Set(features)].slice(0, 8);
    if (!uniqueFeatures.length) return '';

    const preview = uniqueFeatures.slice(0, 2);
    const fullContent = `
      <ul class="pop-series-qv-info__list">
        ${uniqueFeatures.map((feature) => `<li>${escapeHTML(feature)}</li>`).join('')}
      </ul>
      <a class="pop-series-qv-info__link" href="${escapeHTML(product.url)}">Explore Further &gt;</a>
    `;

    return `
      <section class="pop-series-qv__key-features" aria-label="Key Features">
        <div class="pop-series-qv__row-heading">
          <span class="pop-series-qv__row-icon">${iconSVG('target')}</span>
          <span>Key Features</span>
          ${registerInfoPanel('key-features', 'Key Features', fullContent)}
        </div>
        <ul class="pop-series-qv__feature-list">
          ${preview.map((feature) => `<li>${escapeHTML(feature)}</li>`).join('')}
        </ul>
        <a class="pop-series-qv__explore" href="${escapeHTML(product.url)}">Explore Further &gt;</a>
      </section>
    `;
  };

  const quickViewQuantityHTML = () => `
    <div class="pop-series-qv__quantity">
      <span class="pop-series-qv__quantity-label">Quantity</span>
      <div class="pop-series-qv__quantity-control">
        <button type="button" aria-label="Decrease quantity" data-pop-qv-qty-minus>${iconSVG('minus')}</button>
        <input type="number" min="1" step="1" value="1" inputmode="numeric" aria-label="Quantity" data-pop-qv-qty>
        <button type="button" aria-label="Increase quantity" data-pop-qv-qty-plus>${iconSVG('plus')}</button>
      </div>
    </div>
  `;

  const serviceRowsHTML = (product) => {
    const rows = [
      {
        id: 'shipping',
        icon: 'truck',
        title: '2-5 Days Fast Shipping',
        content: `We offer fast shipping straight to your door across the US, UK, and Germany. Orders under $99 will incur a $5 shipping fee. Free shipping is available for orders over $99. It usually takes 2-5 business days for your package to be delivered, so you can sit back and relax, worry-free. Please see our <a href="${escapeHTML(product.shippingPolicyUrl || '/policies/shipping-policy')}">Shipping Policy</a> for more details.`
      },
      {
        id: 'warranty',
        icon: 'shield',
        title: '1 Year Warranty',
        content: `We are committed to delivering high-quality products and exceptional after-sales service. To ensure your satisfaction, we provide a one-year limited warranty for all Poposoap products. Learn more about <a href="${escapeHTML(product.warrantyPolicyUrl || '/pages/one-year-warranty-policy')}">One-Year Warranty Policy</a>.`
      },
      {
        id: 'return',
        icon: 'return',
        title: '30-Day Return',
        content: `If you're not completely satisfied, we offer a full refund within 30 days of purchase (return shipping not included). <a href="${escapeHTML(product.refundPolicyUrl || '/policies/refund-policy')}">See full terms</a>.`
      },
      {
        id: 'support',
        icon: 'support',
        title: 'Lifetime Customer Support',
        content: `We'll always be there for you. If you have any questions or concerns, feel free to <a href="${escapeHTML(product.contactUrl || '/pages/contact-us')}">contact us</a> at <a href="mailto:info@poposoapsolar.com">info@poposoapsolar.com</a>.`
      },
      {
        id: 'sustainability',
        icon: 'leaf',
        title: '3 Sustainability Features',
        content: `This product has sustainability features recognized by trusted certifications.<br><br><strong>Recycled materials:</strong> Contains at least 50% recycled material.<br><strong>Safer chemicals:</strong> Made with chemicals safer for human health and the environment.<br><strong>Worker well-being:</strong> Manufactured on farms or in facilities that protect the rights and/or health of workers.<br><br>As certified by Global Recycled Standard<br>Certification Number: TE-00300591<br>Certification Body: Bureau Veritas`
      }
    ];

    return `
      <div class="pop-series-qv__service-list" aria-label="Purchase support">
        ${rows.map((row) => `
          <div class="pop-series-qv__service-row">
            <span class="pop-series-qv__row-icon">${iconSVG(row.icon)}</span>
            <span>${escapeHTML(row.title)}</span>
            ${registerInfoPanel(row.id, row.title, row.content)}
          </div>
        `).join('')}
      </div>
    `;
  };

  const quickViewPaymentHTML = (product) => `
    <div class="pop-series-qv__ps-wrapper" aria-label="Payment and delivery trust">
      <div class="pop-series-qv__ps-row">
        <div class="pop-series-qv__ps-title">Pay with Ease</div>
        <ul class="pop-series-qv__ps-icons">
          ${product.paymentHtml || ''}
        </ul>
        ${registerInfoPanel('pay-with-ease', 'Pay with Ease', 'Choose from secure payment options at checkout, including major cards and PayPal where available.')}
      </div>
      <div class="pop-series-qv__ps-row">
        <div class="pop-series-qv__ps-title">Trusted Delivery</div>
        <ul class="pop-series-qv__ps-icons pop-series-qv__ps-icons--shipping">
          ${product.trustedDeliveryImage ? `<li><img src="${escapeHTML(product.trustedDeliveryImage)}" alt="trusted delivery" loading="lazy"></li>` : ''}
        </ul>
        ${registerInfoPanel('trusted-delivery', 'Trusted Delivery', 'POPOSOAP works with trusted delivery partners to help your order arrive safely and reliably.')}
      </div>
    </div>
  `;

  const quickViewInfoDialogHTML = () => `
    <div class="pop-series-qv-info" data-pop-qv-info-dialog hidden>
      <div class="pop-series-qv-info__backdrop" data-pop-qv-info-close></div>
      <div class="pop-series-qv-info__panel" role="dialog" aria-modal="true" aria-labelledby="pop-series-qv-info-title">
        <button class="pop-series-qv-info__close" type="button" aria-label="Close information" data-pop-qv-info-close>${iconSVG('close')}</button>
        <h4 id="pop-series-qv-info-title" data-pop-qv-info-title></h4>
        <div class="pop-series-qv-info__content" data-pop-qv-info-content></div>
      </div>
    </div>
  `;

  const quickViewLightboxHTML = (media = []) => {
    if (!media.length) return '';

    return `
      <div class="pop-series-qv-lightbox" data-pop-qv-lightbox hidden>
        <button class="pop-series-qv-lightbox__close" type="button" aria-label="Close image viewer" data-pop-qv-lightbox-close>${iconSVG('close')}</button>
        <div class="pop-series-qv-lightbox__counter" data-pop-qv-lightbox-counter>1 / ${media.length}</div>
        ${media.length > 1 ? `<button class="pop-series-qv-lightbox__arrow pop-series-qv-lightbox__arrow--prev" type="button" aria-label="Previous enlarged image" data-pop-qv-lightbox-prev>${iconSVG('chevronLeft')}</button>` : ''}
        <img class="pop-series-qv-lightbox__image" data-pop-qv-lightbox-image data-pop-qv-swipe-zone data-pop-qv-fallback-src="${escapeHTML(media[0].src || '')}" src="${escapeHTML(media[0].zoomSrc || media[0].src)}" alt="${escapeHTML(media[0].alt || '')}">
        ${media.length > 1 ? `<button class="pop-series-qv-lightbox__arrow pop-series-qv-lightbox__arrow--next" type="button" aria-label="Next enlarged image" data-pop-qv-lightbox-next>${iconSVG('chevronRight')}</button>` : ''}
        ${media.length > 1 ? `
          <div class="pop-series-qv-lightbox__thumbs">
            ${media.map((item, index) => `
              <button class="pop-series-qv-lightbox__thumb" type="button" data-pop-qv-lightbox-media="${index}" aria-current="${index === 0}">
                <img src="${escapeHTML(item.thumb || item.src)}" data-pop-qv-fallback-src="${escapeHTML(item.src || '')}" alt="">
              </button>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `;
  };

  const quickViewFBTHTML = () => '';

  const renderQuickView = () => {
    const product = quickViewState.product;
    const variant = quickViewState.variant;
    const media = Array.isArray(product.media) ? product.media : [];
    const activeMedia = media[quickViewState.mediaIndex] || media[0];
    const quickView = getQuickView();

    quickViewState.infoPanels = {};

    quickView.innerHTML = `
      <div class="pop-series-qv__backdrop" data-pop-qv-close></div>
      <div class="pop-series-qv__panel" role="dialog" aria-modal="true" aria-labelledby="pop-series-qv-title">
        <button class="pop-series-qv__close" type="button" aria-label="Close quick product view" data-pop-qv-close>${iconSVG('close')}</button>

        <div class="pop-series-qv__media">
          <div class="pop-series-qv__stage" data-pop-qv-swipe-zone>
            ${media.length > 1 ? `<button class="pop-series-qv__arrow pop-series-qv__arrow--prev" type="button" data-pop-qv-prev aria-label="Previous product image">${iconSVG('chevronLeft')}</button>` : ''}
            ${activeMedia ? `<button class="pop-series-qv__image-button" type="button" data-pop-qv-image-zoom aria-label="View larger product image"><img class="pop-series-qv__main-image" data-pop-qv-main-image src="${escapeHTML(activeMedia.src)}" alt="${escapeHTML(activeMedia.alt || product.title)}" loading="eager" decoding="async"></button>` : '<div class="pop-series-card__placeholder" aria-hidden="true"></div>'}
            ${media.length > 1 ? `<button class="pop-series-qv__arrow pop-series-qv__arrow--next" type="button" data-pop-qv-next aria-label="Next product image">${iconSVG('chevronRight')}</button>` : ''}
          </div>
          ${media.length > 1 ? `<div class="pop-series-qv__thumbs">${quickViewThumbsHTML(media)}</div>` : ''}
        </div>

        <div class="pop-series-qv__content">
          <h3 class="pop-series-qv__title" id="pop-series-qv-title">${escapeHTML(product.title)}</h3>
          ${quickViewBadgesHTML(product)}
          ${product.ratingCount > 0 ? `
            <a class="pop-series-qv__reviews" href="${escapeHTML(product.url)}#shopify-product-reviews">
              <span class="pop-series-qv__stars" aria-hidden="true">&#9733;&#9733;&#9733;&#9733;&#9733;</span>
              <span>${escapeHTML(product.ratingValue)} (${escapeHTML(product.ratingCount)} reviews)</span>
            </a>
          ` : ''}

          <div class="pop-series-qv__price-row">${variantPriceHTML(variant, product)}</div>
          ${quickViewCouponHTML(product)}
          ${quickViewKeyFeaturesHTML(product)}
          ${quickViewVariantHTML(product)}
          ${quickViewQuantityHTML()}
          ${quickViewFBTHTML()}
          ${serviceRowsHTML(product)}
          ${quickViewPaymentHTML(product)}
        </div>

        <div class="pop-series-qv__bar">
          <div class="pop-series-qv__stock">
            <span class="pop-series-qv__stock-icon" aria-hidden="true">&#10003;</span>
            <span>${variant?.available ? 'In stock - estimated delivery in 2-5 business days.' : 'This option is currently sold out.'}</span>
          </div>
          <div class="pop-series-qv__bar-actions">
            <strong class="pop-series-qv__bar-price" data-pop-qv-bar-price>${escapeHTML(formatMoney(currentUnitPriceCents(), product))}</strong>
            <button class="pop-series-qv__button pop-series-qv__button--add" type="button" data-pop-qv-add ${variant?.available ? '' : 'disabled'}>Add to Cart</button>
            <button class="pop-series-qv__button pop-series-qv__button--buy" type="button" data-pop-qv-buy ${variant?.available ? '' : 'disabled'}>Buy Now</button>
          </div>
          <div class="pop-series-qv__status" role="status" aria-live="polite" data-pop-qv-status></div>
        </div>
      </div>
      ${quickViewInfoDialogHTML()}
      ${quickViewLightboxHTML(media)}
    `;

    initQuickViewCoupons(quickView);
  };

  const openQuickView = (trigger) => {
    const product = productFromTrigger(trigger);
    if (!product) return;

    quickViewState.product = product;
    quickViewState.variant = selectedVariantFor(product);
    quickViewState.mediaIndex = selectedMediaIndexFor(product, quickViewState.variant);
    quickViewState.lastTrigger = trigger;
    quickViewState.hydrationToken += 1;
    const hydrationToken = quickViewState.hydrationToken;

    renderQuickView();
    preloadAdjacentMedia();

    const quickView = getQuickView();
    quickView.classList.add('is-open');
    quickView.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pop-series-qv-open');
    quickView.querySelector('.pop-series-qv__close')?.focus({ preventScroll: true });

    track('collection_quick_view_opened', {
      product_handle: product.handle,
      product_title: product.title
    });

    hydrateQuickViewDetails(product, hydrationToken);
  };

  const closeQuickView = ({ restoreFocus = true } = {}) => {
    const quickView = getQuickView();
    closeInfoPanel();
    quickView.classList.remove('is-open');
    quickView.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('pop-series-qv-open');
    if (restoreFocus) {
      quickViewState.lastTrigger?.focus?.({ preventScroll: true });
    }
  };

  const hydrateQuickViewDetails = async (product, token) => {
    const quickView = getQuickView();
    const fbtTarget = quickView.querySelector('[data-pop-qv-fbt]');
    if (!product?.url) return;

    if (productDetailCache.has(product.url)) {
      const details = productDetailCache.get(product.url);
      if (quickViewState.hydrationToken === token) {
        setQuickViewCoupon(details?.coupon || (product.defaultCoupon?.enabled ? product.defaultCoupon : null));
        if (fbtTarget && details?.fbtHtml) {
          fbtTarget.innerHTML = details.fbtHtml;
          fbtTarget.hidden = false;
          refreshExternalRecommendationWidgets();
          validateRecommendationTarget(fbtTarget, token);
        }
      }
      return;
    }

    try {
      const response = await fetch(product.url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Unable to load product page details.');

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const coupon = extractCouponFromProductPage(doc) || (product.defaultCoupon?.enabled ? product.defaultCoupon : null);
      const fbtHtml = '';

      const details = { coupon, fbtHtml };
      productDetailCache.set(product.url, details);

      if (quickViewState.hydrationToken === token) {
        setQuickViewCoupon(coupon);
        if (fbtTarget && fbtHtml) {
          fbtTarget.innerHTML = fbtHtml;
          fbtTarget.hidden = false;
          validateRecommendationTarget(fbtTarget, token);
        }
      }
    } catch (error) {
      productDetailCache.set(product.url, {
        coupon: product.defaultCoupon?.enabled ? product.defaultCoupon : null,
        fbtHtml: ''
      });
      if (quickViewState.hydrationToken === token) {
        setQuickViewCoupon(product.defaultCoupon?.enabled ? product.defaultCoupon : null);
      }
    }
  };

  const updateQuickViewMedia = (index) => {
    const product = quickViewState.product;
    const media = Array.isArray(product?.media) ? product.media : [];
    if (!media.length) return;

    quickViewState.mediaIndex = (index + media.length) % media.length;
    const activeMedia = media[quickViewState.mediaIndex];
    const quickView = getQuickView();
    const image = quickView.querySelector('[data-pop-qv-main-image]');

    if (image && activeMedia) {
      image.alt = activeMedia.alt || product.title;
      image.classList.add('is-loading');

      const nextImage = new Image();
      nextImage.decoding = 'async';
      nextImage.onload = () => {
        image.src = activeMedia.src;
        image.classList.remove('is-loading');
      };
      nextImage.onerror = () => {
        image.src = activeMedia.src;
        image.classList.remove('is-loading');
      };
      nextImage.src = activeMedia.src;
    }

    quickView.querySelectorAll('[data-pop-qv-media]').forEach((thumb) => {
      thumb.setAttribute('aria-current', String(Number(thumb.dataset.popQvMedia) === quickViewState.mediaIndex));
    });

    updateLightboxMedia();
    preloadAdjacentMedia();
  };

  const updateLightboxMedia = () => {
    const media = Array.isArray(quickViewState.product?.media) ? quickViewState.product.media : [];
    const item = media[quickViewState.mediaIndex];
    const lightbox = getQuickView().querySelector('[data-pop-qv-lightbox]');
    if (!lightbox || !item) return;

    const image = lightbox.querySelector('[data-pop-qv-lightbox-image]');
    const counter = lightbox.querySelector('[data-pop-qv-lightbox-counter]');

    if (image) {
      image.alt = item.alt || quickViewState.product.title || '';
      image.dataset.popQvFallbackSrc = item.src || '';
      delete image.dataset.popQvFallbackApplied;
      image.src = item.zoomSrc || item.src;
    }

    if (counter) {
      counter.textContent = `${quickViewState.mediaIndex + 1} / ${media.length}`;
    }

    lightbox.querySelectorAll('[data-pop-qv-lightbox-media]').forEach((thumb) => {
      thumb.setAttribute('aria-current', String(Number(thumb.dataset.popQvLightboxMedia) === quickViewState.mediaIndex));
    });
  };

  const openLightbox = () => {
    const lightbox = getQuickView().querySelector('[data-pop-qv-lightbox]');
    if (!lightbox) return;
    updateLightboxMedia();
    lightbox.hidden = false;
    lightbox.querySelector('[data-pop-qv-lightbox-close]')?.focus({ preventScroll: true });
  };

  const closeLightbox = () => {
    const lightbox = getQuickView().querySelector('[data-pop-qv-lightbox]');
    if (!lightbox) return;
    lightbox.hidden = true;
    getQuickView().querySelector('[data-pop-qv-image-zoom]')?.focus({ preventScroll: true });
  };

  const bindQuickViewSwipe = () => {
    const quickView = getQuickView();
    let swipe = null;

    const resetSwipe = () => {
      swipe = null;
    };

    quickView.addEventListener('pointerdown', (event) => {
      const zone = event.target.closest('[data-pop-qv-swipe-zone]');
      const media = Array.isArray(quickViewState.product?.media) ? quickViewState.product.media : [];

      if (!zone || media.length < 2 || event.button > 0 || event.target.closest('[data-pop-qv-prev], [data-pop-qv-next], [data-pop-qv-lightbox-prev], [data-pop-qv-lightbox-next]')) {
        resetSwipe();
        return;
      }

      swipe = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        horizontal: false
      };

      zone.setPointerCapture?.(event.pointerId);
    }, { passive: true });

    quickView.addEventListener('pointermove', (event) => {
      if (!swipe || swipe.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - swipe.startX;
      const deltaY = event.clientY - swipe.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      swipe.lastX = event.clientX;
      swipe.lastY = event.clientY;

      if (!swipe.horizontal && absY > 18 && absY > absX * 1.15) {
        resetSwipe();
        return;
      }

      if (absX > 12 && absX > absY * 1.15) {
        swipe.horizontal = true;
        event.preventDefault();
      }
    }, { passive: false });

    quickView.addEventListener('pointerup', (event) => {
      if (!swipe || swipe.pointerId !== event.pointerId) return;
      if (Date.now() < quickViewState.swipeSuppressClickUntil) {
        resetSwipe();
        return;
      }

      const deltaX = event.clientX - swipe.startX;
      const deltaY = event.clientY - swipe.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (absX >= 48 && absX > absY * 1.25) {
        event.preventDefault();
        quickViewState.swipeSuppressClickUntil = Date.now() + 450;
        updateQuickViewMedia(quickViewState.mediaIndex + (deltaX < 0 ? 1 : -1));
      }

      resetSwipe();
    }, { passive: false });

    quickView.addEventListener('pointercancel', resetSwipe, { passive: true });

    let touchSwipe = null;

    quickView.addEventListener('touchstart', (event) => {
      const zone = event.target.closest('[data-pop-qv-swipe-zone]');
      const media = Array.isArray(quickViewState.product?.media) ? quickViewState.product.media : [];
      const touch = event.touches?.[0];

      if (!zone || !touch || media.length < 2 || event.target.closest('[data-pop-qv-prev], [data-pop-qv-next], [data-pop-qv-lightbox-prev], [data-pop-qv-lightbox-next]')) {
        touchSwipe = null;
        return;
      }

      touchSwipe = {
        startX: touch.clientX,
        startY: touch.clientY,
        lastX: touch.clientX,
        lastY: touch.clientY,
        horizontal: false
      };
    }, { passive: true });

    quickView.addEventListener('touchmove', (event) => {
      if (!touchSwipe) return;
      const touch = event.touches?.[0];
      if (!touch) return;

      const deltaX = touch.clientX - touchSwipe.startX;
      const deltaY = touch.clientY - touchSwipe.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      touchSwipe.lastX = touch.clientX;
      touchSwipe.lastY = touch.clientY;

      if (!touchSwipe.horizontal && absY > 18 && absY > absX * 1.15) {
        touchSwipe = null;
        return;
      }

      if (absX > 12 && absX > absY * 1.15) {
        touchSwipe.horizontal = true;
        event.preventDefault();
      }
    }, { passive: false });

    quickView.addEventListener('touchend', (event) => {
      if (!touchSwipe || Date.now() < quickViewState.swipeSuppressClickUntil) {
        touchSwipe = null;
        return;
      }

      const deltaX = touchSwipe.lastX - touchSwipe.startX;
      const deltaY = touchSwipe.lastY - touchSwipe.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (absX >= 44 && absX > absY * 1.2) {
        event.preventDefault();
        quickViewState.swipeSuppressClickUntil = Date.now() + 450;
        updateQuickViewMedia(quickViewState.mediaIndex + (deltaX < 0 ? 1 : -1));
      }

      touchSwipe = null;
    }, { passive: false });

    quickView.addEventListener('touchcancel', () => {
      touchSwipe = null;
    }, { passive: true });
  };

  const trapFocus = (container, event) => {
    if (!container || event.key !== 'Tab') return false;

    const focusable = [...container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((item) => item.offsetParent !== null || item === document.activeElement);

    if (!focusable.length) return false;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return true;
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
      return true;
    }

    return false;
  };

  const updateQuickViewVariant = (variantId) => {
    const product = quickViewState.product;
    const variant = product?.variants?.find((item) => String(item.id) === String(variantId));
    if (!variant) return;

    quickViewState.variant = variant;
    const quickView = getQuickView();

    quickView.querySelector('[data-pop-qv-price]').textContent = variant.price;
    updateQuickViewTotalPrice();

    const compare = quickView.querySelector('[data-pop-qv-compare]');
    const save = quickView.querySelector('[data-pop-qv-save]');

    if (compare) {
      compare.textContent = variant.compareAtPrice || '';
      compare.hidden = !variant.compareAtPrice;
    }

    if (save) {
      save.textContent = variant.saveAmount ? `Save ${variant.saveAmount}` : '';
      save.hidden = !variant.saveAmount;
    }

    quickView.querySelectorAll('[data-pop-qv-add], [data-pop-qv-buy]').forEach((button) => {
      button.disabled = !variant.available;
    });

    if (variant.imageSrc) {
      const mediaIndex = product.media?.findIndex((media) => media.src === variant.imageSrc);
      if (mediaIndex >= 0) {
        updateQuickViewMedia(mediaIndex);
      } else {
        const image = quickView.querySelector('[data-pop-qv-main-image]');
        if (image) image.src = variant.imageSrc;
      }
    }
    preloadAdjacentMedia();
  };

  const getQuickViewQuantity = () => {
    const input = getQuickView().querySelector('[data-pop-qv-qty]');
    const value = Number.parseInt(input?.value, 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const setQuickViewStatus = (message = '', isError = false) => {
    const status = getQuickView().querySelector('[data-pop-qv-status]');
    if (!status) return;
    status.textContent = message;
    status.style.color = isError ? '#c62828' : BRAND_GREEN;
  };

  const openCartDrawerAfterAdd = async (responseJson) => {
    const cartContent = await (await fetch(route('cart.js'))).json();
    cartContent.sections = responseJson.sections;

    document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true,
      detail: {
        items: responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson],
        cart: cartContent
      }
    }));

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'variant:add',
        cart: cartContent
      }
    }));
  };

  const addQuickViewVariant = async ({ checkout = false } = {}) => {
    const product = quickViewState.product;
    const variant = quickViewState.variant;
    if (!product || !variant?.id || !variant.available) return;

    const quickView = getQuickView();
    const buttons = quickView.querySelectorAll('[data-pop-qv-add], [data-pop-qv-buy]');
    const quantity = getQuickViewQuantity();

    buttons.forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    });
    setQuickViewStatus(checkout ? 'Preparing checkout...' : 'Adding to cart...');

    const sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    const formData = new FormData();
    formData.set('id', variant.id);
    formData.set('quantity', String(quantity));
    formData.set('sections', sectionsToBundle.join(','));
    formData.set('sections_url', route(`variants/${variant.id}`));

    try {
      const response = await fetch(route('cart/add.js'), {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || 'Unable to add this item to cart.');
      }

      track(checkout ? 'collection_quick_view_buy_now' : 'collection_quick_view_add_to_cart', {
        product_handle: product.handle,
        product_title: product.title,
        variant_id: variant.id,
        quantity
      });

      if (checkout) {
        window.location.href = route('checkout');
        return;
      }

      await openCartDrawerAfterAdd(responseJson);
      setQuickViewStatus('Added to cart.');
    } catch (error) {
      setQuickViewStatus(error.message || 'Unable to add this item to cart.', true);
    } finally {
      buttons.forEach((button) => {
        button.disabled = !quickViewState.variant?.available;
        button.removeAttribute('aria-busy');
      });
    }
  };

  const updateQuantity = (delta) => {
    const input = getQuickView().querySelector('[data-pop-qv-qty]');
    if (!input) return;
    const nextValue = Math.max(1, getQuickViewQuantity() + delta);
    input.value = String(nextValue);
    updateQuickViewTotalPrice();
  };

  bindQuickViewSwipe();

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-pop-event]');
    if (!target) return;
    if (target.matches('a:not([href]), [aria-disabled="true"]')) return;

    track(target.dataset.popEvent, {
      product_handle: target.dataset.popProductHandle || undefined,
      product_title: target.dataset.popProductTitle || undefined,
      position: target.dataset.popPosition || undefined,
      href: target.getAttribute('href') || undefined
    });
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-pop-quick-view-trigger]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    openQuickView(trigger);
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-pop-qv-info-close]')) {
      closeInfoPanel();
      return;
    }

    const infoButton = event.target.closest('[data-pop-qv-info]');
    if (infoButton) {
      openInfoPanel(infoButton.dataset.popQvInfo);
      return;
    }

    const nativeCouponInfo = event.target.closest('.pop-series-qv__coupon-slot .coupon-info-btn');
    if (nativeCouponInfo) {
      const card = nativeCouponInfo.closest('.pop-series-qv__coupon-slot');
      let modal = null;
      const faqId = nativeCouponInfo.dataset.faqId;

      if (faqId) {
        modal = document.getElementById(faqId);
      }

      modal = modal || card?.querySelector('.coupon-modal');

      if (modal) {
        modal.classList.add('is-active');
        modal.removeAttribute('hidden');
      }
      return;
    }

    const nativeCouponClose = event.target.closest('.pop-series-qv__coupon-slot .coupon-modal__close, .pop-series-qv__coupon-slot .coupon-modal__overlay');
    if (nativeCouponClose) {
      nativeCouponClose.closest('.coupon-modal')?.classList.remove('is-active');
      return;
    }

    const nativeCouponCopy = event.target.closest('.pop-series-qv__coupon-slot .coupon-copy');
    if (nativeCouponCopy) {
      const code = nativeCouponCopy.dataset.code || nativeCouponCopy.closest('.coupon-code-row')?.querySelector('.coupon-code')?.textContent?.trim() || '';
      navigator.clipboard?.writeText(code);
      nativeCouponCopy.classList.add('is-copied');
      nativeCouponCopy.textContent = 'COPIED';
      window.setTimeout(() => {
        nativeCouponCopy.classList.remove('is-copied');
        nativeCouponCopy.textContent = 'COPY CODE';
      }, 1600);
      setQuickViewStatus('Discount code copied.');
      return;
    }

    const copyButton = event.target.closest('[data-pop-qv-copy-code]');
    if (copyButton) {
      navigator.clipboard?.writeText(copyButton.dataset.popQvCopyCode || '');
      setQuickViewStatus('Discount code copied.');
      return;
    }

    if (event.target.closest('[data-pop-qv-image-zoom]')) {
      if (Date.now() < quickViewState.swipeSuppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      openLightbox();
      return;
    }

    if (event.target.closest('[data-pop-qv-lightbox-close]')) {
      closeLightbox();
      return;
    }

    if (event.target.closest('[data-pop-qv-lightbox-prev]')) {
      updateQuickViewMedia(quickViewState.mediaIndex - 1);
      return;
    }

    if (event.target.closest('[data-pop-qv-lightbox-next]')) {
      updateQuickViewMedia(quickViewState.mediaIndex + 1);
      return;
    }

    const lightboxMediaButton = event.target.closest('[data-pop-qv-lightbox-media]');
    if (lightboxMediaButton) {
      updateQuickViewMedia(Number(lightboxMediaButton.dataset.popQvLightboxMedia));
      return;
    }

    if (event.target.closest('[data-pop-qv-close]')) {
      closeQuickView();
      return;
    }

    if (event.target.closest('[data-pop-qv-prev]')) {
      updateQuickViewMedia(quickViewState.mediaIndex - 1);
      return;
    }

    if (event.target.closest('[data-pop-qv-next]')) {
      updateQuickViewMedia(quickViewState.mediaIndex + 1);
      return;
    }

    const mediaButton = event.target.closest('[data-pop-qv-media]');
    if (mediaButton) {
      updateQuickViewMedia(Number(mediaButton.dataset.popQvMedia));
      return;
    }

    if (event.target.closest('[data-pop-qv-qty-minus]')) {
      updateQuantity(-1);
      return;
    }

    if (event.target.closest('[data-pop-qv-qty-plus]')) {
      updateQuantity(1);
      return;
    }

    if (event.target.closest('[data-pop-qv-add]')) {
      addQuickViewVariant({ checkout: false });
      return;
    }

    if (event.target.closest('[data-pop-qv-buy]')) {
      addQuickViewVariant({ checkout: true });
    }
  });

  document.addEventListener('change', (event) => {
    const selector = event.target.closest('[data-pop-qv-variant]');
    if (!selector) return;
    updateQuickViewVariant(selector.value);
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-pop-qv-qty]');
    if (!input) return;
    input.value = String(Math.max(1, getQuickViewQuantity()));
    updateQuickViewTotalPrice();
  });

  document.addEventListener('error', (event) => {
    const image = event.target;
    if (!(image instanceof HTMLImageElement) || !image.matches('[data-pop-qv-fallback-src]')) return;

    const fallbackSrc = image.dataset.popQvFallbackSrc;
    if (!fallbackSrc || image.dataset.popQvFallbackApplied === 'true' || image.src === fallbackSrc) return;

    image.dataset.popQvFallbackApplied = 'true';
    image.src = fallbackSrc;
  }, true);

  document.addEventListener('keydown', (event) => {
    const quickView = document.querySelector('[data-pop-quick-view].is-open');
    if (!quickView) return;

    const activeLightbox = quickView.querySelector('[data-pop-qv-lightbox]:not([hidden])');
    const activeInfoDialog = quickView.querySelector('[data-pop-qv-info-dialog]:not([hidden])');

    if (event.key === 'Tab') {
      if (activeLightbox && trapFocus(activeLightbox, event)) return;
      if (activeInfoDialog && trapFocus(activeInfoDialog, event)) return;
      trapFocus(quickView.querySelector('.pop-series-qv__panel'), event);
      return;
    }

    if (event.key === 'Escape') {
      if (activeLightbox) {
        closeLightbox();
      } else if (activeInfoDialog) {
        closeInfoPanel();
      } else {
        closeQuickView();
      }
    }

    if (event.key === 'ArrowLeft') {
      updateQuickViewMedia(quickViewState.mediaIndex - 1);
    }

    if (event.key === 'ArrowRight') {
      updateQuickViewMedia(quickViewState.mediaIndex + 1);
    }
  });

  document.addEventListener('change', (event) => {
    const sortSelect = event.target.closest('[data-pop-sort-select]');
    if (sortSelect) {
      track('collection_sort_change', { sort_by: sortSelect.value });
      sortSelect.form?.requestSubmit?.();
      if (!sortSelect.form?.requestSubmit) sortSelect.form?.submit();
      return;
    }

    const input = event.target.closest('[data-pop-filter-input]');
    if (!input) return;

    track('collection_filter_apply_or_change', {
      filter_name: input.name,
      filter_value: input.value,
      checked: input.checked
    });
  });

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-pop-filter-toggle]');
    if (!toggle) return;

    const root = toggle.closest('[data-pop-series-products]');
    if (!root) return;

    const isOpen = root.classList.toggle('is-filter-open');
    toggle.setAttribute('aria-expanded', String(isOpen));

    const label = toggle.querySelector('span:not(.pop-series-mobile-filter__count)');
    if (label) {
      label.textContent = isOpen
        ? (toggle.dataset.closeLabel || 'Close Filter')
        : (toggle.dataset.openLabel || 'Open Filter');
    }

    if (isOpen) {
      root.querySelector('[data-pop-filter-panel] summary')?.focus({ preventScroll: true });
    }
  });

  document.addEventListener('click', (event) => {
    const applyButton = event.target.closest('[data-pop-filter-apply]');
    if (!applyButton) return;

    const root = applyButton.closest('[data-pop-series-products]');
    const toggle = root?.querySelector('[data-pop-filter-toggle]');
    if (!root || !toggle) return;

    root.classList.remove('is-filter-open');
    toggle.setAttribute('aria-expanded', 'false');

    const label = toggle.querySelector('span:not(.pop-series-mobile-filter__count)');
    if (label) {
      label.textContent = toggle.dataset.openLabel || 'Open Filter';
    }

    root.querySelector('.pop-series-grid')?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start'
    });
  });

  const bindVideoFallbacks = (scope = document) => {
    scope.querySelectorAll('.pop-series-special-card--video video').forEach((video) => {
      if (video.dataset.popVideoFallbackBound === 'true') return;
      video.dataset.popVideoFallbackBound = 'true';

      video.addEventListener('error', () => {
        video.closest('.pop-series-special-card--video')?.classList.add('is-video-failed');
      });
    });
  };

  const bindVideoVisibility = (scope = document) => {
    const videos = scope.querySelectorAll('.pop-series-special-card--video video');
    if (!videos.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      videos.forEach((video) => {
        video.removeAttribute('autoplay');
        video.pause();
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => video.play().catch(() => {}));
      return;
    }

    if (!window.__poposoapSeriesVideoObserver) {
      window.__poposoapSeriesVideoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, { threshold: [0, 0.35, 0.75] });
    }

    videos.forEach((video) => {
      if (video.dataset.popVideoObserverBound === 'true') return;
      video.dataset.popVideoObserverBound = 'true';
      window.__poposoapSeriesVideoObserver.observe(video);
    });
  };

  const bindBestSellerDots = (scope = document) => {
    scope.querySelectorAll('.pop-series-best').forEach((section) => {
      const track = section.querySelector('.pop-series-best__track');
      const cards = [...section.querySelectorAll('.pop-series-best__track .pop-series-card')];
      if (!track || cards.length < 2 || section.querySelector('.pop-series-best__dots')) return;

      const dots = document.createElement('div');
      dots.className = 'pop-series-best__dots';
      dots.setAttribute('aria-label', 'Best seller carousel position');

      cards.forEach((card, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pop-series-best__dot';
        dot.setAttribute('aria-label', `Go to best seller ${index + 1}`);
        dot.setAttribute('aria-current', String(index === 0));
        dot.addEventListener('click', () => {
          card.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'start'
          });
        });
        dots.appendChild(dot);
      });

      section.querySelector('.pop-series__inner')?.appendChild(dots);

      const setActiveDot = (activeIndex) => {
        dots.querySelectorAll('.pop-series-best__dot').forEach((dot, index) => {
          dot.setAttribute('aria-current', String(index === activeIndex));
        });
      };

      if ('IntersectionObserver' in window) {
        const ratios = new Map();
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            ratios.set(entry.target, entry.intersectionRatio);
          });

          let activeIndex = 0;
          let activeRatio = -1;
          cards.forEach((card, index) => {
            const ratio = ratios.get(card) || 0;
            if (ratio > activeRatio) {
              activeRatio = ratio;
              activeIndex = index;
            }
          });
          setActiveDot(activeIndex);
        }, {
          root: track,
          threshold: [0, 0.35, 0.65, 0.95]
        });

        cards.forEach((card) => observer.observe(card));
      }
    });
  };

  const bindReviewCarousel = (scope = document) => {
    scope.querySelectorAll('[data-pop-series-reviews]').forEach((viewport) => {
      if (viewport.dataset.popReviewsBound === 'true') return;
      viewport.dataset.popReviewsBound = 'true';

      const track = viewport.querySelector('[data-pop-series-reviews-track]');
      if (!track) return;

      const scrollByCard = (direction) => {
        const card = track.querySelector('.pop-series-review-card');
        const amount = card ? card.getBoundingClientRect().width + 18 : track.clientWidth * 0.8;
        track.scrollBy({
          left: amount * direction,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      };

      viewport.querySelector('[data-pop-reviews-prev]')?.addEventListener('click', () => scrollByCard(-1));
      viewport.querySelector('[data-pop-reviews-next]')?.addEventListener('click', () => scrollByCard(1));
    });
  };

  const bindTrustCarousel = (scope = document) => {
    scope.querySelectorAll('[data-pop-series-section="trust"]').forEach((section) => {
      const track = section.querySelector('[data-pop-trust-track]');
      const bar = section.querySelector('[data-pop-trust-progress]');
      if (!track || !bar || track.dataset.popTrustBound === 'true') return;
      track.dataset.popTrustBound = 'true';

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const mobile = window.matchMedia('(max-width: 767px)');
      let timer = null;
      let resumeTimer = null;

      const updateProgress = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const width = track.scrollWidth > 0 ? Math.min(100, (track.clientWidth / track.scrollWidth) * 100) : 100;
        const available = 100 - width;
        const left = maxScroll > 0 ? (track.scrollLeft / maxScroll) * available : 0;
        bar.style.setProperty('--pop-trust-progress-width', `${width}%`);
        bar.style.setProperty('--pop-trust-progress-left', `${left}%`);
      };

      const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = null;
      };

      const start = () => {
        stop();
        if (!mobile.matches || reduceMotion.matches || document.hidden) return;
        if (track.scrollWidth <= track.clientWidth + 4) return;
        timer = window.setInterval(() => {
          const maxScroll = track.scrollWidth - track.clientWidth;
          const next = track.scrollLeft + track.clientWidth >= maxScroll - 4 ? 0 : track.scrollLeft + track.clientWidth;
          track.scrollTo({ left: next, behavior: 'smooth' });
        }, 3600);
      };

      const pauseThenResume = () => {
        stop();
        if (resumeTimer) window.clearTimeout(resumeTimer);
        resumeTimer = window.setTimeout(start, 4200);
      };

      track.addEventListener('scroll', () => window.requestAnimationFrame(updateProgress), { passive: true });
      track.addEventListener('pointerdown', pauseThenResume, { passive: true });
      track.addEventListener('focusin', stop);
      track.addEventListener('focusout', start);
      window.addEventListener('resize', () => {
        updateProgress();
        start();
      });
      document.addEventListener('visibilitychange', start);

      updateProgress();
      start();
    });
  };

  const bindSeries = (scope = document) => {
    applyDefaultSort(scope);
    bindVideoFallbacks(scope);
    bindVideoVisibility(scope);
    bindTrustCarousel(scope);
    bindBestSellerDots(scope);
    bindReviewCarousel(scope);
  };

  const applyDefaultSort = (scope = document) => {
    scope.querySelectorAll?.('[data-pop-sort-form][data-pop-default-sort]').forEach((form) => {
      const select = form.querySelector('[data-pop-sort-select]');
      const defaultSort = form.dataset.popDefaultSort;
      if (!select || !defaultSort || select.value) return;
      select.value = defaultSort;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindSeries());
  } else {
    bindSeries();
  }

  document.addEventListener('shopify:section:load', (event) => bindSeries(event.target));
})();
